/**
 * ROUND 15 I7 -- a supervisory check's label and sentence, in the
 * reader's words.
 *
 * The server composes each check's `detail` in English and, beside it,
 * the same sentence as catalogued fragments (`detail_said`): a key and
 * the figures it interpolates. This renders the fragments. The English
 * stays the fallback, so a key the catalogue has not learned -- or an
 * older server that sends no fragments -- still reads.
 */
import type { TFunction } from "i18next";

import { i18n } from "shared/i18n";

export interface Said {
  key: string;
  params?: Record<string, unknown>;
}

interface Sentence {
  detail: string;
  detail_said?: Said[];
  detail_join?: string;
}

/** Values that are vocabulary rather than data, said in the reader's words. */
function worded(params: Record<string, unknown>): Record<string, unknown> {
  const out = { ...params };
  const top = params.topClass;
  if (typeof top === "string" && i18n.exists(`supervisory.topClass.${top}`)) {
    out.topClass = i18n.t(`supervisory.topClass.${top}`);
  }
  return out;
}

export function checkDetail(t: TFunction, c: Sentence): string {
  const said = c.detail_said ?? [];
  if (said.length === 0 || !said.every((s) => i18n.exists(s.key))) return c.detail;
  const join =
    c.detail_join === "slash" || c.detail_join === "colon" ? c.detail_join : "dash";
  const sep = t(`supervisory.join.${join}`);
  return said.map((s) => t(s.key, worded(s.params ?? {}))).join(sep);
}

export function checkLabel(t: TFunction, c: { key: string; label: string }): string {
  // Round 16 I9 -- "architecture:<key>": ":" is i18next's namespace
  // separator, so the catalogue spells it "__".
  const key = `supervisory.label.${c.key.replace(":", "__")}`;
  return i18n.exists(key) ? t(key) : c.label;
}
